/**
 * E-commerce Analytics Dashboard.
 *
 * Displays Shopify (and eventually Amazon) analytics for a brand:
 * KPI cards, revenue trend, daily analytics, top products, customer metrics.
 * Data comes from Azure SQL views via /api/ecommerce endpoints.
 */

import { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { powerbiRequest } from "../authConfig";
import { Loading } from "../ui-components/Loading";
import { ErrorComponent } from "../ui-components/ErrorComponent";
import {
  isDemoMode,
  getEcommerceOverview,
  getEcommerceRevenueTrend,
  getEcommerceProducts,
  getEcommerceCustomers,
  clearApiCache,
  EcommerceOverview,
  MonthlyRevenue,
  ProductABC,
  CohortRow,
} from "../utils/PowerBIApiCall";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  LabelList,
} from "recharts";
import { getPortcoIdFromBrand } from "../utils/brandMapping";
import { PortcoSubNav } from "../ui-components/PortcoSubNav";
import {
  formatCurrencyCompact as fmtCurrency,
  formatCurrency as fmtCurrencyFull,
  formatPctFromFraction as fmtPct,
  formatNumber as fmtNum,
} from "../utils/format";
import { KpiCard, Delta } from "../ui-components/KpiCard";
import { SectionLabel } from "../ui-components/SectionLabel";
import { RefreshButton } from "../ui-components/RefreshButton";
import { SERIES, GRID, AXIS_TICK, TOOLTIP_STYLE } from "../utils/chartTheme";

// ============================================================================
// Revenue Trend Chart
// ============================================================================

/**
 * Renders the YoY % above each current-year bar; hidden when YoY is null.
 * Typed loosely because recharts' LabelList `content` passes a wide prop bag.
 */
function YoyBarLabel(props: {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  value?: string | number | null;
}) {
  const x = Number(props.x ?? 0);
  const y = Number(props.y ?? 0);
  const width = Number(props.width ?? 0);
  const value = props.value;
  if (value === null || value === undefined) return null;
  const num = Number(value);
  const up = num >= 0;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fontSize={11}
      fontWeight={600}
      fill={up ? "#059669" : "#dc2626"}
    >
      {`${up ? "+" : ""}${num.toFixed(0)}%`}
    </text>
  );
}

function RevenueTrendChart({ data }: { data: MonthlyRevenue[] }) {
  // Build current year + prior year comparison
  const chartData = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const current = data.filter(
      (d) => d.year === currentYear || d.year === currentYear - 1,
    );

    // Group by month, merge CY and PY
    const byMonth = new Map<
      number,
      { month: number; label: string; cy: number; py: number }
    >();
    for (const row of current) {
      const monthNum = row.month;
      const existing = byMonth.get(monthNum) || {
        month: monthNum,
        label: new Date(2000, monthNum - 1).toLocaleString("en", {
          month: "short",
        }),
        cy: 0,
        py: 0,
      };
      if (row.year === currentYear) {
        existing.cy = row.net_revenue;
      } else {
        existing.py = row.net_revenue;
      }
      byMonth.set(monthNum, existing);
    }

    return Array.from(byMonth.values())
      .sort((a, b) => a.month - b.month)
      .map((r) => ({
        ...r,
        // YoY % only when both years have real revenue for that month
        yoy: r.py > 0 && r.cy > 0 ? ((r.cy - r.py) / r.py) * 100 : null,
      }));
  }, [data]);

  if (chartData.length === 0) return null;

  const hasYoy = chartData.some((d) => d.yoy !== null);

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Monthly Revenue — Current Year vs Prior Year
      </h3>
      <div style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="label" tick={AXIS_TICK} />
            <YAxis tick={AXIS_TICK} tickFormatter={fmtCurrency} width={70} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value: any, name: any) => [
                fmtCurrencyFull(Number(value) || 0),
                name === "cy" ? "Current Year" : "Prior Year",
              ]}
            />
            <Legend
              formatter={(value: string) =>
                value === "cy" ? "Current Year" : "Prior Year"
              }
            />
            <Bar dataKey="cy" fill={SERIES.primary} radius={[4, 4, 0, 0]}>
              {hasYoy && (
                <LabelList dataKey="yoy" content={YoyBarLabel as never} />
              )}
            </Bar>
            <Line
              dataKey="py"
              type="monotone"
              stroke="#94a3b8"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: "#94a3b8", r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Daily Analytics Chart
// ============================================================================

function DailyAnalyticsChart({ data }: { data: EcommerceOverview["daily"] }) {
  const chartData = useMemo(() => {
    return data.map((d) => ({
      ...d,
      dateLabel: new Date(d.report_date).toLocaleDateString("en", {
        month: "short",
        day: "numeric",
      }),
    }));
  }, [data]);

  // A stalled feed leaves the trailing window empty. Show an explicit
  // placeholder rather than a collapsed chart, which looks like a rendering
  // bug and leaves the charts row lopsided.
  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4 flex flex-col">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Daily Revenue (Last 30 Days)
        </h3>
        <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
          <svg
            className="w-8 h-8 text-gray-300 mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 3v18h18M7 14l3-3 3 3 5-6"
            />
          </svg>
          <p className="text-sm text-gray-500 font-medium">
            No daily sales in the last 30 days
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Monthly figures above remain current.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Daily Revenue (Last 30 Days)
      </h3>
      <div style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={SERIES.primary}
                  stopOpacity={0.3}
                />
                <stop offset="95%" stopColor={SERIES.primary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="dateLabel" tick={AXIS_TICK} interval={4} />
            <YAxis tick={AXIS_TICK} tickFormatter={fmtCurrency} width={65} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value: any, name: any) => {
                const v = Number(value) || 0;
                if (name === "net_revenue")
                  return [fmtCurrencyFull(v), "Revenue"];
                if (name === "orders") return [v, "Orders"];
                return [v, name];
              }}
            />
            <Area
              type="monotone"
              dataKey="net_revenue"
              stroke={SERIES.primary}
              strokeWidth={2}
              fill="url(#revenueGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Top Products Table
// ============================================================================

const TOP_PRODUCT_LIMIT = 20;

function TopProductsTable({ products }: { products: ProductABC[] }) {
  const shown = products.slice(0, TOP_PRODUCT_LIMIT);
  const shownSharePct = shown.reduce((s, p) => s + p.pct_of_revenue, 0);
  const abcColors: Record<string, string> = {
    A: "bg-emerald-100 text-emerald-800",
    B: "bg-amber-100 text-amber-800",
    C: "bg-gray-100 text-gray-600",
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          Top Products (ABC Analysis)
        </h3>
        {products.length > 0 && (
          <span className="text-xs text-gray-400">
            Top {shown.length} of {products.length} · {fmtPct(shownSharePct)} of
            revenue
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm tabular-nums">
          <thead>
            <tr className="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-right">Revenue</th>
              <th className="px-3 py-2 text-right">Units</th>
              <th className="px-3 py-2 text-right">Orders</th>
              <th className="px-3 py-2 text-right">% of Rev</th>
              <th className="px-3 py-2 text-center">Class</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr
                key={p.product_key}
                className="border-t border-gray-100 hover:bg-gray-50"
              >
                <td className="px-3 py-2 text-gray-400 font-mono text-xs">
                  {p.revenue_rank}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900 truncate max-w-[190px]">
                    {p.product_title}
                  </div>
                  <div className="text-[11px] text-gray-400">{p.vendor}</div>
                </td>
                <td className="px-3 py-2 text-right font-medium">
                  {fmtCurrencyFull(p.total_revenue)}
                </td>
                <td className="px-3 py-2 text-right text-gray-600">
                  {fmtNum(p.total_units)}
                </td>
                <td className="px-3 py-2 text-right text-gray-600">
                  {fmtNum(p.order_count)}
                </td>
                <td className="px-3 py-2 text-right text-gray-600">
                  {fmtPct(p.pct_of_revenue)}
                </td>
                <td className="px-3 py-2 text-center">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${abcColors[p.abc_class] || "bg-gray-100 text-gray-600"}`}
                  >
                    {p.abc_class}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {products.length > TOP_PRODUCT_LIMIT && (
        <div className="px-4 py-2 border-t border-gray-100 text-center text-xs text-gray-400">
          +{products.length - TOP_PRODUCT_LIMIT} more products not shown
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Conversion & Sessions Chart
// ============================================================================

function ConversionChart({ data }: { data: EcommerceOverview["daily"] }) {
  const chartData = useMemo(() => {
    return data.map((d) => ({
      dateLabel: new Date(d.report_date).toLocaleDateString("en", {
        month: "short",
        day: "numeric",
      }),
      sessions: d.sessions,
      conversionRate:
        d.conversion_rate == null ? null : d.conversion_rate * 100,
      orders: d.orders,
    }));
  }, [data]);

  // Trailing days whose session analytics haven't landed yet (~2-day lag)
  const pendingDays = chartData.filter((d) => d.sessions == null).length;

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">
          Sessions &amp; Conversion Rate
        </h3>
        {pendingDays > 0 && (
          <span className="text-xs text-gray-400">
            Last {pendingDays} day{pendingDays > 1 ? "s" : ""} pending —
            sessions finalize on a ~2-day lag
          </span>
        )}
      </div>
      <div style={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="dateLabel" tick={AXIS_TICK} interval={4} />
            <YAxis
              yAxisId="left"
              tick={AXIS_TICK}
              tickFormatter={(v: number) => v.toLocaleString()}
              width={55}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={AXIS_TICK}
              tickFormatter={(v: number) => `${v.toFixed(1)}%`}
              width={45}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value: any, name: any) => {
                const v = Number(value) || 0;
                if (name === "Conv Rate") return [`${v.toFixed(2)}%`, name];
                return [v.toLocaleString(), name];
              }}
            />
            <Legend />
            <Bar
              yAxisId="left"
              dataKey="sessions"
              fill={SERIES.primarySoft}
              radius={[2, 2, 0, 0]}
              name="Sessions"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="conversionRate"
              stroke={SERIES.primary}
              strokeWidth={2}
              dot={false}
              name="Conv Rate"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================================
// Cohort Retention Table
// ============================================================================

const RETENTION_SCALE = [
  { min: 0.5, className: "bg-emerald-200 text-emerald-900", label: "≥50%" },
  { min: 0.2, className: "bg-emerald-100 text-emerald-800", label: "≥20%" },
  { min: 0.1, className: "bg-amber-100 text-amber-800", label: "≥10%" },
  {
    min: Number.MIN_VALUE,
    className: "bg-gray-100 text-gray-600",
    label: ">0%",
  },
];

function retentionColor(pct: number): string {
  const match = RETENTION_SCALE.find((s) => pct >= s.min);
  return match ? match.className : "bg-gray-50 text-gray-400";
}

function CohortTable({ cohorts }: { cohorts: CohortRow[] }) {
  // Group by cohort_month
  const cohortMonths = useMemo(() => {
    const map = new Map<string, CohortRow[]>();
    for (const row of cohorts) {
      const existing = map.get(row.cohort_month) || [];
      existing.push(row);
      map.set(row.cohort_month, existing);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 6);
  }, [cohorts]);

  const maxPeriod = Math.max(...cohorts.map((c) => c.months_since_first), 0);
  const periods = Array.from(
    { length: Math.min(maxPeriod + 1, 7) },
    (_, i) => i,
  );

  if (cohortMonths.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          Cohort Retention
        </h3>
        <span className="text-xs text-gray-400">
          % of cohort ordering again in month N
        </span>
      </div>
      <div className="p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm tabular-nums">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
                <th className="py-2 pr-4">Cohort</th>
                <th className="py-2 pr-4 text-right">Size</th>
                {periods.map((p) => (
                  <th key={p} className="py-2 px-2 text-center">
                    M{p}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohortMonths.map(([month, rows]) => {
                const byPeriod = new Map(
                  rows.map((r) => [r.months_since_first, r]),
                );
                return (
                  <tr key={month} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium text-gray-700">
                      {month}
                    </td>
                    <td className="py-2 pr-4 text-right text-gray-600">
                      {(byPeriod.get(0)?.cohort_size || 0).toLocaleString()}
                    </td>
                    {periods.map((p) => {
                      const row = byPeriod.get(p);
                      if (!row) {
                        return (
                          <td
                            key={p}
                            className="py-2 px-2 text-center text-gray-300"
                          >
                            —
                          </td>
                        );
                      }
                      return (
                        <td key={p} className="py-2 px-2 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${retentionColor(row.retention_pct)}`}
                          >
                            {(row.retention_pct * 100).toFixed(1)}%
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Color scale legend */}
        <div className="flex items-center gap-2 mt-3">
          {RETENTION_SCALE.map((s) => (
            <span
              key={s.label}
              className={`px-2 py-0.5 rounded text-[10px] font-medium ${s.className}`}
            >
              {s.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Dashboard UI
// ============================================================================

function EcommerceDashboardUI({
  brandCode,
  portcoId,
}: {
  brandCode: string;
  portcoId?: string;
}) {
  const [overview, setOverview] = useState<EcommerceOverview | null>(null);
  const [revenueTrend, setRevenueTrend] = useState<MonthlyRevenue[]>([]);
  const [products, setProducts] = useState<ProductABC[]>([]);
  const [cohorts, setCohorts] = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadData() {
    setError(null);
    try {
      const [ov, rev, prods, cust] = await Promise.all([
        getEcommerceOverview(brandCode),
        getEcommerceRevenueTrend(brandCode),
        getEcommerceProducts(brandCode),
        getEcommerceCustomers(brandCode),
      ]);
      setOverview(ov);
      setRevenueTrend(rev);
      setProducts(prods);
      setCohorts(cust.cohorts);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadData().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [brandCode]);

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await clearApiCache();
      await loadData();
    } finally {
      setIsRefreshing(false);
    }
  }

  if (loading) return <Loading />;
  if (error)
    return (
      <div className="max-w-md mx-auto mt-8 p-6 rounded-xl border border-red-200 bg-red-50">
        <h3 className="text-red-800 font-semibold">Error</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
      </div>
    );
  if (!overview)
    return (
      <div className="max-w-md mx-auto mt-8 p-6 rounded-xl border border-gray-200/70 bg-gray-50">
        <p className="text-gray-600">No data available</p>
      </div>
    );

  const { currentMonth, priorMonth, repeatRate, daily } = overview;

  // The overview snapshot only carries year_month ("2026-07"); the numeric
  // year/month columns exist only on revenue-trend rows. Parse it here.
  const [curYear, curMonth] = currentMonth
    ? currentMonth.year_month.split("-").map(Number)
    : [0, 0];

  // Prior-year same month, for YoY growth. Falls back to null when history
  // is absent — <Delta> then renders nothing.
  const priorYear =
    currentMonth && curYear
      ? (revenueTrend.find(
          (r) => r.year === curYear - 1 && r.month === curMonth,
        ) ?? null)
      : null;

  // Compute KPIs from latest daily data
  const latestDay = daily.length > 0 ? daily[daily.length - 1] : null;
  const last7 = daily.slice(-7);
  const last7Revenue = last7.reduce((s, d) => s + d.net_revenue, 0);
  const last7Orders = last7.reduce((s, d) => s + d.orders, 0);
  const last7Sessions = last7.reduce((s, d) => s + (d.sessions ?? 0), 0);
  // Days still awaiting the ~2-day-lagged sessions feed have a null
  // conversion_rate; exclude them so pending days don't drag the average to 0.
  const convDays = last7.filter((d) => d.conversion_rate != null);
  const last7AvgConv =
    convDays.length > 0
      ? convDays.reduce((s, d) => s + (d.conversion_rate ?? 0), 0) /
        convDays.length
      : 0;

  const periodLabel =
    currentMonth && curYear
      ? new Date(curYear, curMonth - 1).toLocaleString("en-US", {
          month: "short",
          year: "numeric",
        })
      : "";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={portcoId ? `/portco/${portcoId}` : "/dashboard"}
            className="text-sm text-wmp-600 hover:text-wmp-700 hover:underline"
          >
            &larr; {portcoId ? "Back" : "Dashboard"}
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mt-1">
            {brandCode.toUpperCase()} — E-commerce Analytics
          </h1>
          {latestDay && (
            <p className="text-sm text-gray-500">
              Data through{" "}
              {new Date(latestDay.report_date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          )}
        </div>
        <RefreshButton onClick={handleRefresh} refreshing={isRefreshing} />
      </div>

      {/* Sub-nav when accessed via portco route */}
      {portcoId && <PortcoSubNav portcoId={portcoId} />}

      {/* ============================ Sales & Growth ==================== */}
      <SectionLabel
        label="Sales & Growth"
        subtitle={periodLabel ? `Current period: ${periodLabel}` : undefined}
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="MTD Revenue"
          value={fmtCurrencyFull(currentMonth?.net_revenue ?? 0)}
          deltas={
            currentMonth && (
              <>
                <Delta
                  current={currentMonth.net_revenue}
                  prior={priorYear?.net_revenue}
                  label="YoY"
                />
                <Delta
                  current={currentMonth.net_revenue}
                  prior={priorMonth?.net_revenue}
                  label="MoM"
                />
              </>
            )
          }
        />
        <KpiCard
          label="MTD Orders"
          value={fmtNum(currentMonth?.orders ?? 0)}
          deltas={
            currentMonth && (
              <>
                <Delta
                  current={currentMonth.orders}
                  prior={priorYear?.orders}
                  label="YoY"
                />
                <Delta
                  current={currentMonth.orders}
                  prior={priorMonth?.orders}
                  label="MoM"
                />
              </>
            )
          }
        />
        <KpiCard
          label="Avg Order Value"
          value={fmtCurrencyFull(currentMonth?.avg_order_value ?? 0)}
          deltas={
            currentMonth && (
              <Delta
                current={currentMonth.avg_order_value}
                prior={priorYear?.avg_order_value}
                label="YoY"
              />
            )
          }
          footnote={
            currentMonth
              ? `${fmtNum(currentMonth.unique_customers)} unique customers`
              : undefined
          }
        />
        <KpiCard
          label="7-Day Revenue"
          value={fmtCurrencyFull(last7Revenue)}
          footnote={`${fmtNum(last7Orders)} orders`}
        />
      </div>

      {/* Revenue trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenueTrendChart data={revenueTrend} />
        <DailyAnalyticsChart data={daily} />
      </div>

      {/* ======================== Traffic & Conversion ================== */}
      <SectionLabel
        label="Traffic & Conversion"
        subtitle="Sessions finalize on a ~2-day lag"
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="7-Day Sessions"
          value={fmtNum(last7Sessions)}
          footnote={
            convDays.length < last7.length
              ? `${last7.length - convDays.length} day(s) pending`
              : undefined
          }
        />
        <KpiCard
          label="Avg Conversion"
          value={fmtPct(last7AvgConv)}
          footnote="last 7 reported days"
        />
        <KpiCard
          label="Repeat Rate"
          value={repeatRate ? fmtPct(repeatRate.repeat_rate) : "—"}
          footnote={
            repeatRate
              ? `${repeatRate.avg_orders_per_customer.toFixed(1)} orders/customer`
              : undefined
          }
        />
        <KpiCard
          label="MTD Refunds"
          value={fmtCurrencyFull(currentMonth?.total_refunded ?? 0)}
          valueColor={
            (currentMonth?.total_refunded ?? 0) > 0
              ? "text-red-600"
              : "text-gray-900"
          }
          footnote={
            currentMonth && currentMonth.net_revenue > 0
              ? `${fmtPct(currentMonth.total_refunded / currentMonth.net_revenue)} of net revenue`
              : undefined
          }
        />
      </div>

      {/* Sessions & Conversion */}
      <ConversionChart data={daily} />

      {/* ====================== Products & Customers ==================== */}
      <SectionLabel
        label="Products & Customers"
        subtitle="Revenue concentration and repeat behavior"
      />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <TopProductsTable products={products} />
        <CohortTable cohorts={cohorts} />
      </div>
    </div>
  );
}

// ============================================================================
// Exported Component (handles auth wrapper)
// ============================================================================

export function EcommerceDashboard() {
  const { brandCode } = useParams<{ brandCode: string }>();
  const { pathname } = useLocation();

  // Detect if accessed via /portco/dtc/:brandCode and resolve parent portco
  const isPortcoRoute = pathname.startsWith("/portco/dtc/");
  const portcoId =
    isPortcoRoute && brandCode ? getPortcoIdFromBrand(brandCode) : undefined;

  if (!brandCode) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 rounded-xl border border-gray-200/70 bg-gray-50">
        <p className="text-gray-600">No brand specified</p>
      </div>
    );
  }

  if (isDemoMode()) {
    return <EcommerceDashboardUI brandCode={brandCode} portcoId={portcoId} />;
  }

  // Always render inside MsalAuthenticationTemplate so MSAL owns the auth
  // timing: it waits for any in-flight interaction/initialization and attempts
  // silent SSO before ever redirecting. A manual `accounts.length` guard here
  // fired a sign-in redirect whenever accounts read empty mid-flight (right
  // after navigating to the tab), which is why DTC re-prompted while the other
  // tabs — which use this same unconditional pattern — did not.
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={powerbiRequest}
      loadingComponent={Loading}
      errorComponent={ErrorComponent}
    >
      <EcommerceDashboardUI brandCode={brandCode} portcoId={portcoId} />
    </MsalAuthenticationTemplate>
  );
}
